/**
 * Sayz Ölçü Etiketi Taksonomisi (kanonik, MVP).
 *
 * Merchant "Ölçü Grupları" ekranında her ölçü satırı için bu etiketlerden
 * birini seçer VEYA hiç seçmez — seçmezse "Ürün Ölçüsü" olarak yorumlanır
 * (ürüne ait, kişiye ait değil, wizard sorusu tetiklemez, skorlamada
 * kullanılmaz).
 *
 * İki ayrı grup var:
 *   • Vücut Ölçüsü (`body`): Göğüs/Bel/Kalça/Omuz — kullanıcıya "vücut tipi"
 *     sorusu açılır (dar / normal / geniş) ve skorlamada kullanılır.
 *   • Ürün Boyu (`product_length`): Ürün Boyu / Kol Boyu / İç Bacak — kullanıcı
 *     boyunu (`height`) ZORUNLU hale getirir; ölçünün kullanıcı boyuyla
 *     eşleşmesi beden skorlamasına katkıda bulunur.
 *
 * Etiket üç tüketiciyi besler:
 *   1) Bedenimi Bul sihirbazı → sadece etiketli ölçüler için ilgili soruyu
 *      tetikler. "Ürün Ölçüsü" satırları sorusuz geçilir.
 *   2) Beden Tablosu "Nasıl Ölçülür" bloğu → her satırın kendi
 *      `howToMeasure` açıklamasını render eder (etiketten bağımsız).
 *      Boşsa satır bloğa gönderilmez.
 *   3) Size suggester → vücut ölçülerini çevre match'e, ürün boyu
 *      ölçülerini height match'e sokar.
 *
 * Not: `kind` string'i backend'e dict olarak yazılır; şema değişmez —
 * `measures: List[Dict[str, Any]]` zaten esnek. `kind` yoksa "Ürün Ölçüsü"
 * anlamına gelir; UI ve wizard bu varsayımı paylaşır. `half: true` flag'i
 * ise merchant tabloyu yarım çevre girerse (örn. "Bel ½ 42.5") kullanılır.
 */

// prettier-ignore
export const MEASUREMENT_KINDS = [
    // ── Vücut Ölçüleri (çevre, kullanıcıya "vücut tipi" sorulur) ────────
    { value: "chest",           label: "Göğüs",           group: "body",           wizardHint: "Göğüs tipini soracağız" },
    { value: "waist",           label: "Bel",             group: "body",           wizardHint: "Bel/karın tipini soracağız" },
    { value: "hip",             label: "Kalça",           group: "body",           wizardHint: "Kalça tipini soracağız" },
    { value: "shoulder",        label: "Omuz",            group: "body",           wizardHint: "Omuz tipini soracağız" },
    // ── Ürün Boyu Ölçüleri (dikey uzunluk, kullanıcı boyunu tetikler) ──
    { value: "product_length",  label: "Ürün Boyu",       group: "product_length", wizardHint: "Kullanıcı boyu zorunlu olur" },
    { value: "product_sleeve",  label: "Kol Boyu (Ürün)", group: "product_length", wizardHint: "Kullanıcı boyu zorunlu olur" },
    { value: "product_inseam",  label: "İç Bacak (Ürün)", group: "product_length", wizardHint: "Kullanıcı boyu zorunlu olur" },
];

export const MEASUREMENT_KIND_MAP = Object.fromEntries(
    MEASUREMENT_KINDS.map((k) => [k.value, k])
);

// Dropdown gruplamada kullanılacak başlıklar.
export const KIND_GROUP_LABELS = {
    body: "Vücut Ölçüsü",
    product_length: "Ürün Boyu",
};

// Kanonik vücut kind'ları (wizard "body:X" adımını tetikler).
export const BODY_KIND_VALUES = MEASUREMENT_KINDS
    .filter((k) => k.group === "body")
    .map((k) => k.value);

// Ürün boyu kind'ları (kullanıcı boyunu zorunlu kılar).
export const PRODUCT_LENGTH_KIND_VALUES = MEASUREMENT_KINDS
    .filter((k) => k.group === "product_length")
    .map((k) => k.value);

// "Ürün Ölçüsü" — vücut ölçüsü değil, ürüne ait. Etiketi olmayan tüm satırlar
// bu anlama gelir. Ayrı bir sabit `value` yok; UI'da `kind` boş/undefined
// olduğunda bu label gösterilir.
export const PRODUCT_MEASURE_LABEL = "Ürün Ölçüsü";
export const PRODUCT_MEASURE_HINT = "Vücut sorusu tetiklemez";

// Wizard sadece body kinds için body:X adımı tetikler.
export const isBodyKind = (kind) => BODY_KIND_VALUES.includes(kind);

// Ürün boyu kind'ları — kullanıcı boyunu `required` yapar.
export const isProductLengthKind = (kind) => PRODUCT_LENGTH_KIND_VALUES.includes(kind);

// Herhangi bir "etiketli" kind mı? (vücut veya ürün boyu)
export const isTaggedKind = (kind) => Boolean(MEASUREMENT_KIND_MAP[kind]);
